/**
 * Task API Service
 *
 * Dispatch Console 页面的任务 API 服务层
 * 用于替换 mockContainerData 静态数据
 */

import api from './api';

/**
 * IANA zone this browser is displaying times in (e.g. 'America/Los_Angeles').
 * Appointment forms collect a wall clock; the backend can only turn that into
 * an instant if it knows which zone the clock was read from.
 */
const browserTimeZone = (): string | undefined => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;   // ancient browser — backend falls back to APP_TIMEZONE
  }
};

// ==================== Types ====================

export interface TaskFilters {
  status?: string;
  task_type?: string;
  driver_id?: number;
  /** All tasks on one order — what the detail modal's "Related Tasks" needs. */
  order_id?: number;
  shipper_id?: number;
  search?: string;
  date_from?: string;
  date_to?: string;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
  limit?: number;
  offset?: number;
}

export interface BackendTask {
  id: number;
  task_id: string;
  order_id?: number;
  container_id?: number;
  driver_id?: number;
  truck_id?: number;
  task_type: string;
  task_subtype?: string;
  status: string;
  // Container info (from JOIN)
  container_number?: string;
  shipping_line?: string;
  shipping_line_code?: string;
  container_size?: string;
  container_type?: string;
  // 要哪种发电机（来自 orders，不是 containers）：genset / genset_chassis /
  // none；null = 没人说过。
  genset_type?: string | null;
  container_status?: string;
  bol_number?: string;
  booking_number?: string;
  container_weight?: number;
  // Driver info (from JOIN)
  driver_name?: string;
  driver_phone?: string;
  driver_status?: string;
  driver_email?: string;
  driver_license?: string;
  driver_user_id?: number;
  // Port info
  port_id?: number;
  port_name?: string;
  port_short_name?: string;
  port_code?: string;
  port_city?: string;
  port_state?: string;
  port_contact_name?: string;
  port_contact_phone?: string;
  port_contact_email?: string;
  port_appointment_time?: string;
  /** Migration 174 — 「当天，不限时间点」；为 true 时不要显示 00:00 */
  port_appointment_all_day?: boolean;
  gate_code?: string;
  lfd?: string;
  port_location_type?: string;
  port_address?: string;
  port_pin?: string;
  port_container_location?: string;
  // Warehouse info (from JOIN)
  warehouse_id?: number;
  warehouse_name?: string;
  warehouse_short_name?: string;
  warehouse_address?: string;
  warehouse_city?: string;
  warehouse_state?: string;
  warehouse_contact_name?: string;
  warehouse_contact_phone?: string;
  warehouse_contact_email?: string;
  warehouse_appointment?: string;
  /** Migration 174 — see port_appointment_all_day */
  warehouse_appointment_all_day?: boolean;
  warehouse_appointment_number?: string;
  warehouse_pin?: string;
  warehouse_container_location?: string;
  warehouse_appointment_required?: boolean;
  // Shipper info (from JOIN)
  shipper_id?: number;
  shipper_name?: string;
  shipper_short_name?: string;
  shipper_contact_name?: string;
  shipper_contact_phone?: string;
  shipper_contact_email?: string;
  shipper_address?: string;
  // Other fields
  chassis_id?: number;
  chassis_number?: string;
  chassis_company?: string;
  seal_number?: string;
  weight?: number;
  current_location?: string;
  dispatcher_notes?: string;
  driver_notes?: string;
  dispatcher_instructions?: string;
  to_driver_notes?: string;
  next_action?: string;
  actual_start_time?: string;
  actual_end_time?: string;
  created_at: string;
  updated_at?: string;
  // Truck info
  truck_plate?: string;
  // Three-party role fields (from Order JOIN)
  ordering_party_type?: 'client' | 'shipper' | 'warehouse' | 'port';
  ordering_party_id?: number;
  ordering_party_name?: string;
  ordering_party_contact_name?: string;
  ordering_party_contact_phone?: string;
  ordering_party_contact_email?: string;
  ordering_party_address?: string;
  bill_to_type?: 'client' | 'shipper' | 'warehouse' | 'port';
  bill_to_id?: number;
  bill_to_name?: string;
  bill_to_contact_name?: string;
  bill_to_contact_phone?: string;
  bill_to_contact_email?: string;
  bill_to_address?: string;
  deliver_to_type?: 'client' | 'shipper' | 'warehouse' | 'port';
  deliver_to_id?: number;
  deliver_to_name?: string;
  deliver_to_contact_name?: string;
  deliver_to_contact_phone?: string;
  deliver_to_contact_email?: string;
  deliver_to_address?: string;
  // Task location polymorphic fields
  origin_type?: 'port' | 'warehouse' | 'shipper' | 'yard';
  origin_id?: number;
  origin_name?: string;
  origin_short_name?: string;
  origin_city?: string;      // "City, ST" — null when both are empty
  origin_address?: string;   // Street address of the origin facility
  destination_type?: 'port' | 'warehouse' | 'shipper' | 'yard';
  destination_id?: number;
  destination_name?: string;
  destination_short_name?: string;
  destination_city?: string;
  destination_address?: string;
  // Appointment enhancement fields
  port_appointment_end?: string;
  port_appointment_status?: 'pending' | 'scheduled' | 'confirmed' | 'completed';
  warehouse_appointment_status?: 'pending' | 'scheduled' | 'confirmed' | 'completed';
  eta_resume?: string;
  // Pause tracking fields
  paused_at?: string;
  paused_from_status?: string;
  pause_reason?: string;
  pause_location?: string;
  total_pause_minutes?: number;
  pause_reason_detail?: string;
  // Yard info (from JOIN) - for Route Node display
  yard_id?: number;
  yard_name?: string;
  yard_short_name?: string;
  yard_city?: string;
  yard_state?: string;
  yard_address?: string;
  yard_contact_name?: string;
  yard_contact_phone?: string;
  yard_contact_email?: string;
  // Shipper polymorphic name (resolved from shipper_type)
  shipper_polymorphic_name?: string;
  shipper_type?: 'client' | 'shipper' | 'warehouse' | 'port';
  import_export?: 'import' | 'export' | 'n/a';
  // Last known driver GPS (from mobile app)
  last_gps_lat?: number;
  last_gps_lng?: number;
  last_gps_time?: string;
  // Multi-node route fields (for accurate display after reroute)
  route_nodes?: string | Array<{ type: string; id?: number; name: string; address?: string }>;
  original_route_nodes?: string;
  route_modified_at?: string;
  route_modification_count?: number;
  route_node_count?: number;
  // Pending delivery fields (for Liveload route display)
  pending_yard_storage?: boolean;
  pending_delivery_destination_type?: string;
  pending_delivery_destination_id?: number;
  // Return location fields (for Liveload tasks)
  return_location_type?: string;
  return_location_id?: number;
  return_location_pending?: boolean;
  // Resolved return location name fields (from API JOIN)
  return_location_name?: string;
  return_location_short_name?: string;
  return_location_city?: string;
  return_location_address?: string;
  // PCC Inspection fields
  pcc_location_id?: number | null;
  pcc_location_name?: string;
  pcc_location_address?: string;
  pcc_location_city?: string;
  pcc_hold_at?: string;
  pcc_hold_reason?: string;
  pending_pcc_inspection?: boolean;
  // Order info (from JOIN)
  order_display_id?: string;
  order_status?: string;
  order_type?: string;
  order_created_at?: string;
  // Paired task info (from JOIN)
  paired_task_id?: number;
  paired_task_display_id?: string;
  paired_task_type?: string;
  paired_task_subtype?: string;
  paired_task_status?: string;
  paired_container_number?: string;
  // Zone & coordinate fields (for dispatch bobtail preview)
  origin_zone_id?: number;
  destination_zone_id?: number;
  origin_lat?: string;
  origin_lng?: string;
  destination_lat?: string;
  destination_lng?: string;
  return_location_zone_id?: number;
  return_location_lat?: string;
  return_location_lng?: string;
  // Distance & Bobtail fields
  estimated_distance_miles?: number;
  actual_distance_miles?: number;
  is_bobtail_leg?: boolean;
  bobtail_from_zone_id?: number;
  bobtail_to_zone_id?: number;
  // 司机卡里手排的位次（迁移 192）
  driver_sort_day?: string | null;
  driver_sort_order?: number | null;
  // 收尾的那一天（迁移 212）。DB 触发器在状态改到 completed/cancelled 那一刻钉上，
  // 退回未收尾就清空。跑完又没有任何预约的活，全靠它才算得出自己属于哪一天。
  completed_day?: string | null;
}

/**
 * 确认窗口上的一段：某位司机、某一天，从上到下的任务。
 *
 * 派车确认的那一刻整张计划按段发给后端，司机卡和司机 App 之后都照它排 ——
 * 日期和次序都是调度在那个窗口里定的，不再各自按预约时间重算一遍。
 */
export interface DriverDayPlanSection {
  driverId: number;
  /** 日分段的 key，'YYYY-MM-DD' */
  day: string;
  taskIds: number[];
}

export interface TaskStats {
  total: number;
  pending: number;
  assigned: number;
  in_progress: number;
  completed: number;
  cancelled: number;
  today: number;
  completed_today: number;
}

interface TaskStatsByStatus {
  total: number;
  vessel: number;
  onHold: number;
  released: number;
  unassigned: number;
  assigned: number;
  picking: number;
  delivering: number;
  onStopYard: number;
  loading: number;
  empty: number;
  loaded: number;
  returning: number;
  completed: number;
  cancelled: number;
  pause: number;
  yardStored: number;
  pccHold: number;
  marked: number;
}

interface PaginatedTaskResponse {
  data: BackendTask[];
  total: number;
  limit: number;
  offset: number;
}

interface TaskUpdateData {
  status?: string;
  driver_id?: number;
  truck_id?: number;
  dispatcher_notes?: string;
  dispatcher_instructions?: string;
  current_location?: string;
  next_action?: string;
  port_appointment_time?: string;
  warehouse_appointment?: string;
  gate_code?: string;
  lfd?: string;
  seal_number?: string;
  [key: string]: string | number | undefined;
}

export interface PairingCandidate {
  id: number;
  task_id: string;
  task_type: string;
  task_subtype: string;
  status: string;
  port_appointment_time?: string;
  order_id?: number;
  container_number?: string;
  port_name?: string;
  port_short_name?: string;
}

/**
 * 为什么这一张要核，以及改不改得了。
 *
 * 前三种能当场改（unlinked 那条会先带人去 eModal 把票找出来）；
 * 后两种改不了，但**必须说出来** —— 悄悄跳过等于没查。
 */
export type PlateCheckReason =
  | 'mismatch'      // 票上写的是别人的车牌
  | 'unverified'    // 票上的车牌从没核过（多半是抢位时的占位车牌）
  | 'unlinked'      // 还没跟 eModal 那张票对上号
  | 'no_truck'      // 司机名下没登记车
  | 'no_match_key'; // 既没柜号也没票号，在 eModal 上认不出来

/**
 * 一张「码头票上的车牌还是别人的」。
 *
 * 换司机之后码头那张票不会自己变 —— 改它要开浏览器、由插件驱动 eModal 页面，
 * 后端做不到。派完车问一次后端「这一批里哪几张要改」，回来的就是这个。
 */
export interface PlateCheckItem {
  appointmentId: number;
  taskId: number;
  taskNumber: string | null;
  containerNumber: string | null;
  terminalName: string | null;
  driverId: number | null;
  driverName: string | null;
  /** 票上现在写着的（上一个司机的，或者抢位时的占位车牌） */
  currentPlate: string | null;
  /** 现在这个司机的车牌；司机名下没登记车时为 null */
  newPlate: string | null;
  reason: PlateCheckReason;
  /**
   * 车牌是**票级**字段：一还一提两条腿共用一次进闸，在 eModal 上改一次两条腿
   * 一起变。排队之前按它去重，否则同一张票要人点两遍（第二遍还会被插件那把
   * 单会话锁挡住）。
   */
  emodalTruckVisitId: number | string | null;
}

// ==================== API Functions ====================

export const taskAPI = {
  /**
   * 获取任务列表
   * GET /api/tasks
   */
  getAll: async (filters: TaskFilters = {}): Promise<PaginatedTaskResponse> => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, String(value));
      }
    });
    const response = await api.get(`/tasks?${params}`);
    return response.data;
  },

  /**
   * 获取单个任务详情
   * GET /api/tasks/:id
   */
  getById: async (id: number): Promise<BackendTask> => {
    const response = await api.get(`/tasks/${id}`);
    return response.data;
  },

  /**
   * 创建新任务
   * POST /api/tasks
   */
  create: async (data: Partial<BackendTask>): Promise<BackendTask> => {
    const response = await api.post('/tasks', data);
    return response.data;
  },

  /**
   * 更新任务
   * PUT /api/tasks/:id
   */
  update: async (id: number, data: TaskUpdateData): Promise<BackendTask> => {
    const response = await api.put(`/tasks/${id}`, data);
    return response.data;
  },

  /**
   * 删除任务
   * DELETE /api/tasks/:id
   */
  delete: async (id: number): Promise<BackendTask> => {
    const response = await api.delete(`/tasks/${id}`);
    return response.data;
  },

  /**
   * 分配司机到任务
   * POST /api/tasks/:id/assign
   */
  assignDriver: async (taskId: number, driverId: number, truckId?: number, force?: boolean): Promise<BackendTask> => {
    const response = await api.post(`/tasks/${taskId}/assign`, {
      driver_id: driverId,
      truck_id: truckId,
      // Overrides the appointment-conflict guard, which otherwise answers 409.
      // Only send it when the dispatcher explicitly chose to assign anyway —
      // the backend records every override on the task timeline.
      ...(force ? { force: true } : {})
    });
    return response.data;
  },

  /**
   * 取消分配司机
   * POST /api/tasks/:id/unassign
   */
  unassignDriver: async (taskId: number): Promise<BackendTask> => {
    const response = await api.post(`/tasks/${taskId}/unassign`);
    return response.data;
  },

  /**
   * 保存司机卡里手排的任务顺序（某司机的某一天）
   * PUT /api/tasks/driver-order
   *
   * taskIds 传那一天从上到下的完整顺序；后端按下标写位次。
   */
  setDriverDayOrder: async (
    driverId: number,
    day: string,
    taskIds: number[]
  ): Promise<{ driverId: number; day: string; taskIds: number[]; skipped: number }> => {
    const response = await api.put('/tasks/driver-order', {
      driverId,
      day,
      taskIds,
    });
    return response.data;
  },

  /**
   * 把确认窗口发出去那一刻的整张计划存下来（每位司机、每一天、从上到下）
   * PUT /api/tasks/driver-order/plan
   *
   * 和 setDriverDayOrder 的差别：这里的任务多半**还没派出去**（派车要等这一批
   * 约完票），所以后端那边不按司机筛。司机卡和司机 App 之后都照这份排。
   */
  setDriverDayPlan: async (
    sections: DriverDayPlanSection[]
  ): Promise<{ sections: DriverDayPlanSection[] }> => {
    const response = await api.put('/tasks/driver-order/plan', { sections });
    return response.data;
  },

  /**
   * 保存任务池里手排的顺序（排活模式，某一天）
   * PUT /api/tasks/pool-order
   *
   * taskIds 传那一天从上到下的完整顺序；后端按下标写位次。
   * 存的是「计划哪天跑、那天的第几个」，和司机无关 —— 所以这些任务派出去之后，
   * 在司机卡上还是同一天，不需要再同步一次。顺序全公司共用。
   */
  setPoolDayOrder: async (
    day: string,
    taskIds: number[]
  ): Promise<{ day: string; taskIds: number[] }> => {
    const response = await api.put('/tasks/pool-order', { day, taskIds });
    return response.data;
  },

  /**
   * 把这几张任务退回「待配」—— 不定在哪天跑了
   * PUT /api/tasks/pool-order，day 传 null
   *
   * 没有这条路的话，拖上去是单行道：定错了一天就再也回不去。
   */
  clearPoolDay: async (taskIds: number[]): Promise<{ day: null; taskIds: number[] }> => {
    const response = await api.put('/tasks/pool-order', { day: null, taskIds });
    return response.data;
  },

  /**
   * 恢复某一天的默认顺序 —— 把手排的痕迹清掉，回到系统自己算的样子
   * PUT /api/tasks/pool-order/reset
   *
   * **日子和位次一起清**：按钮叫「恢复默认」，用户期待的就是回到没动过的样子；
   * 只清位次的话，排上去的空柜还赖在那一天不走（2026-09-24 实测）。
   */
  resetPoolDayOrder: async (
    day: string,
    taskIds: number[]
  ): Promise<{ day: string; taskIds: number[] }> => {
    const response = await api.put('/tasks/pool-order/reset', { day, taskIds });
    return response.data;
  },

  /**
   * 开始任务
   * POST /api/tasks/:id/start
   */
  startTask: async (taskId: number): Promise<BackendTask> => {
    const response = await api.post(`/tasks/${taskId}/start`);
    return response.data;
  },

  /**
   * 完成任务 (使用 transition 端点以支持 Task 2 自动创建)
   * POST /api/tasks/:id/transition
   */
  completeTask: async (taskId: number, driverNotes?: string): Promise<BackendTask> => {
    const response = await api.post(`/tasks/${taskId}/transition`, {
      status: 'completed',
      event_data: driverNotes ? { driver_notes: driverNotes } : undefined
    });
    return response.data;
  },

  /**
   * 取消任务
   * POST /api/tasks/:id/cancel
   */
  cancelTask: async (taskId: number, reason?: string): Promise<BackendTask> => {
    const response = await api.post(`/tasks/${taskId}/cancel`, { reason });
    return response.data;
  },

  /**
   * 获取任务统计
   * GET /api/tasks/stats
   */
  getStats: async (): Promise<TaskStats> => {
    const response = await api.get('/tasks/stats');
    return response.data;
  },

  /**
   * 获取按状态分组的统计 (Phase 2 - for Dispatch Console)
   * GET /api/tasks/stats/by-status
   */
  getStatsByStatus: async (): Promise<TaskStatsByStatus> => {
    const response = await api.get('/tasks/stats/by-status');
    return response.data;
  },

  /**
   * 更新任务状态 (Phase 2 - 支持 Task 2 自动创建)
   * PATCH /api/tasks/:id/status
   * 注意: 此方法现在使用 TaskStateMachine，会触发 Task 2 自动创建
   * 注意: 不能用于 cancelled/completed 状态的任务，请使用 unlockStatus
   */
  updateStatus: async (taskId: number, status: string): Promise<BackendTask> => {
    const response = await api.patch(`/tasks/${taskId}/status`, { status });
    return response.data;
  },

  /**
   * 解锁终端状态任务 (cancelled/completed)
   * POST /api/tasks/:id/unlock
   * 用于修改已取消或已完成的任务状态（需要确认）
   */
  unlockStatus: async (
    taskId: number,
    status: string,
    reason?: string
  ): Promise<BackendTask> => {
    const response = await api.post(`/tasks/${taskId}/unlock`, {
      status,
      reason,
      confirmed: true
    });
    return response.data;
  },

  /**
   * 智能状态更新 - 自动检测是否需要解锁
   * 如果当前状态是 cancelled/completed，会自动调用 unlockStatus
   * 否则调用普通的 updateStatus
   */
  smartUpdateStatus: async (
    taskId: number,
    newStatus: string,
    currentStatus: string,
    unlockReason?: string
  ): Promise<BackendTask> => {
    const terminalStatuses = ['cancelled', 'completed'];
    if (terminalStatuses.includes(currentStatus)) {
      return taskAPI.unlockStatus(taskId, newStatus, unlockReason);
    }
    return taskAPI.updateStatus(taskId, newStatus);
  },

  /**
   * 状态转换 (推荐方法 - 完整支持状态机功能)
   * POST /api/tasks/:id/transition
   * 用于需要完整状态验证和副作用处理的场景
   */
  transitionStatus: async (
    taskId: number,
    status: string,
    options?: {
      reason?: string;
      gps_lat?: number;
      gps_lng?: number;
      event_data?: Record<string, unknown>;
    }
  ): Promise<BackendTask> => {
    const response = await api.post(`/tasks/${taskId}/transition`, {
      status,
      ...options
    });
    return response.data;
  },

  /**
   * 更新 next_action
   * PATCH /api/tasks/:id/next-action
   */
  updateNextAction: async (taskId: number, nextAction: string): Promise<BackendTask> => {
    const response = await api.patch(`/tasks/${taskId}/next-action`, { next_action: nextAction });
    return response.data;
  },

  /**
   * 添加文件到任务 (JSON body, 需要 file_url)
   * POST /api/tasks/:id/files
   */
  addFile: async (taskId: number, fileData: {
    file_type: string;
    file_url: string;
    file_name?: string;
    category?: 'document' | 'image';
    notes?: string;
  }): Promise<Record<string, unknown>> => {
    const response = await api.post(`/tasks/${taskId}/files`, fileData);
    return response.data;
  },

  /**
   * 上传文件到任务 (multipart form)
   * POST /api/tasks/:id/files/upload
   */
  uploadFile: async (taskId: number, file: File, fileType: string, category: 'document' | 'image' = 'document'): Promise<Record<string, unknown>> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('file_type', fileType);
    formData.append('category', category);
    const response = await api.post(`/tasks/${taskId}/files/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  /**
   * 获取任务文件
   * GET /api/tasks/:id/files
   */
  getFiles: async (taskId: number, category?: 'document' | 'image'): Promise<Record<string, unknown>[]> => {
    const params = category ? `?category=${category}` : '';
    const response = await api.get(`/tasks/${taskId}/files${params}`);
    return response.data;
  },

  /**
   * 删除任务文件
   * DELETE /api/tasks/:id/files/:fileId
   */
  deleteFile: async (taskId: number, fileId: number): Promise<Record<string, unknown>> => {
    const response = await api.delete(`/tasks/${taskId}/files/${fileId}`);
    return response.data;
  },

  // ==================== Phase 4: Batch Operations ====================

  /**
   * 批量更新任务状态
   * POST /api/tasks/batch/status
   */
  batchUpdateStatus: async (taskIds: number[], status: string): Promise<BackendTask[]> => {
    const response = await api.post('/tasks/batch/status', { task_ids: taskIds, status });
    return response.data;
  },

  /**
   * 批量分配司机
   * POST /api/tasks/batch/assign
   */
  batchAssignDriver: async (taskIds: number[], driverId: number, truckId?: number, force?: boolean): Promise<BackendTask[]> => {
    const response = await api.post('/tasks/batch/assign', {
      task_ids: taskIds,
      driver_id: driverId,
      truck_id: truckId,
      // See assignDriver — explicit dispatcher override of the conflict guard.
      ...(force ? { force: true } : {})
    });
    return response.data;
  },

  /**
   * 派完车复核车牌 —— 这一批里哪几张的码头票上还写着别人的车牌。
   * POST /api/tasks/batch/plate-check
   *
   * 只读。判据和任务行里那个红色卡车按钮同源（后端 PLATE_FIX_APPT_CONDITIONS），
   * 所以这里回空就是真的没有要改的，不是「查不到」。
   */
  checkPlates: async (taskIds: number[]): Promise<PlateCheckItem[]> => {
    const response = await api.post('/tasks/batch/plate-check', { task_ids: taskIds });
    return response.data;
  },

  /**
   * 批量删除任务
   * POST /api/tasks/batch/delete
   */
  batchDelete: async (taskIds: number[]): Promise<BackendTask[]> => {
    const response = await api.post('/tasks/batch/delete', { task_ids: taskIds });
    return response.data;
  },

  // ==================== Payment Estimate ====================

  /**
   * 获取任务的预估支付信息（基于 salary rate 匹配）
   * GET /api/dispatch-tasks/:id/payment-estimate
   */
  getPaymentEstimate: async (taskId: number): Promise<{
    source: 'actual' | 'salary_model' | 'default';
    base_rate: number;
    extra_rate: number;
    total: number;
    payment_status?: string;
    extras: { type: string; description: string; amount: number; quantity?: number; unit?: string; unit_rate?: number | null }[];
    waiting_time_info?: { free_hours: number; unit_minutes: number; rate_per_unit: number } | null;
    origin_name?: string;
    destination_name?: string;
  }> => {
    const response = await api.get(`/dispatch-tasks/${taskId}/payment-estimate`);
    return response.data;
  },

  // ==================== Yard Storage Operations ====================

  /**
   * 将容器存入 Yard (调度安排)
   * POST /api/dispatch-tasks/:id/store-to-yard
   *
   * 当调度员决定将容器暂存到 yard 时使用
   * 会创建一个新的 delivery 任务（yard → warehouse）
   *
   * @param taskId - 当前任务 ID
   * @param yardId - 目标 yard ID
   * @param reason - 存入原因（可选）
   * @param scheduledDeliveryTime - 计划送货时间（可选）
   */
  storeToYard: async (
    taskId: number,
    yardId: number,
    reason?: string,
    scheduledDeliveryTime?: string
  ): Promise<{
    task: BackendTask;
    yardId: number;
    reason?: string;
    scheduledDeliveryTime?: string;
    note: string;
  }> => {
    const response = await api.post(`/dispatch-tasks/${taskId}/store-to-yard`, {
      yard_id: yardId,
      reason,
      scheduled_delivery_time: scheduledDeliveryTime
    });
    return response.data;
  },

  /**
   * 提前把下一程放出来，不等这一程跑完 —— 调度今天要排明天的活。
   *
   * 放出来的那一行可以派、可以上板、司机能看见，但箱子真的落场之前不能开跑。
   * POST /api/dispatch-tasks/:id/release-next-leg
   */
  releaseNextLeg: async (taskId: number): Promise<{ task: BackendTask }> => {
    const response = await api.post(`/dispatch-tasks/${taskId}/release-next-leg`);
    return response.data;
  },

  /**
   * Cancel pending store-to-yard and restore original destination
   * POST /api/dispatch-tasks/:id/cancel-store-to-yard
   */
  cancelStoreToYard: async (taskId: number): Promise<BackendTask> => {
    const response = await api.post(`/dispatch-tasks/${taskId}/cancel-store-to-yard`);
    return response.data;
  },

  /**
   * Send container to PCC for inspection
   * POST /api/dispatch-tasks/:id/send-to-pcc
   */
  sendToPCC: async (
    taskId: number,
    pccLocationId: number,
    reason?: string,
    scheduledDeliveryTime?: string
  ): Promise<{
    task: BackendTask;
    pccLocationId: number;
    pccLocationName?: string;
    reason?: string;
    scheduledDeliveryTime?: string;
    note: string;
  }> => {
    const response = await api.post(`/dispatch-tasks/${taskId}/send-to-pcc`, {
      pcc_location_id: pccLocationId,
      reason,
      scheduled_delivery_time: scheduledDeliveryTime
    });
    return response.data;
  },

  // ==================== Appointment Operations ====================

  /**
   * Set port appointment
   * POST /api/dispatch-tasks/:id/appointments/port
   *
   * WP4-b: modal now captures appointmentNumber / pin / containerLocation on
   * top of the original (date, times, gateCode). All new fields are optional
   * so older callers stay working.
   */
  setPortAppointment: async (
    taskId: number,
    data: {
      date: string;
      startTime: string;
      endTime: string;
      /** 「当天，不限时间点」：后端会锚到 00:00 并丢掉时间窗 */
      allDay?: boolean;
      gateCode?: string;
      pin?: string;
      containerLocation?: string;
      appointmentNumber?: string;
      nodeIndex?: number;
    }
  ): Promise<BackendTask> => {
    const response = await api.post(`/dispatch-tasks/${taskId}/appointments/port`, {
      date: data.date,
      start_time: data.startTime,
      end_time: data.endTime,
      all_day: data.allDay,
      gate_code: data.gateCode,
      pin: data.pin,
      container_location: data.containerLocation,
      appointment_number: data.appointmentNumber,
      node_index: data.nodeIndex,
      // date/start_time/end_time are a wall clock read off this browser's
      // calendar, so name the zone they belong to. The backend needs it to
      // resolve them to an instant; without it the operator's zone is assumed
      // to be the terminal's. Omitted by non-browser callers (eModal receipts),
      // which fall back to APP_TIMEZONE.
      time_zone: browserTimeZone(),
    });
    return response.data;
  },

  /**
   * Set warehouse appointment
   * POST /api/dispatch-tasks/:id/appointments/warehouse
   */
  setWarehouseAppointment: async (
    taskId: number,
    data: {
      date: string;
      time: string;
      /** 见 setPortAppointment */
      allDay?: boolean;
      appointmentNumber?: string;
      pin?: string;
      containerLocation?: string;
    }
  ): Promise<BackendTask> => {
    const response = await api.post(`/dispatch-tasks/${taskId}/appointments/warehouse`, {
      date: data.date,
      time: data.time,
      all_day: data.allDay,
      appointment_number: data.appointmentNumber,
      pin: data.pin,
      container_location: data.containerLocation,
      time_zone: browserTimeZone(),   // see setPortAppointment
    });
    return response.data;
  },

  /**
   * Pause a task
   * PATCH /api/dispatch-tasks/:id/pause
   *
   * @param taskId - Task ID
   * @param data - Pause data
   */
  pauseTask: async (
    taskId: number,
    data?: {
      reason?: string;
      note?: string;
      etaResume?: string;
    }
  ): Promise<BackendTask> => {
    const response = await api.patch(`/dispatch-tasks/${taskId}/pause`, {
      reason: data?.reason,
      note: data?.note,
      eta_resume: data?.etaResume
    });
    return response.data;
  },

  /**
   * Resume a paused task
   * PATCH /api/dispatch-tasks/:id/resume
   *
   * @param taskId - Task ID
   */
  resumeTask: async (taskId: number): Promise<BackendTask> => {
    const response = await api.patch(`/dispatch-tasks/${taskId}/resume`);
    return response.data;
  },

  /**
   * Update ETA resume for a paused task
   * PATCH /api/dispatch-tasks/:id/eta-resume
   *
   * @param taskId - Task ID
   * @param etaResume - ETA resume datetime
   */
  updateEtaResume: async (
    taskId: number,
    etaResume: string
  ): Promise<BackendTask> => {
    const response = await api.patch(`/dispatch-tasks/${taskId}/eta-resume`, {
      eta_resume: etaResume
    });
    return response.data;
  },

  /**
   * Reschedule a paused task
   * POST /api/dispatch-tasks/:id/reschedule
   *
   * @param taskId - Task ID
   * @param data - Reschedule data
   */
  rescheduleTask: async (
    taskId: number,
    data: {
      destinationType?: string;
      destinationId?: number;
      warehouseAppointment?: string;
      portAppointment?: string;
      autoResume?: boolean;
    }
  ): Promise<BackendTask> => {
    const response = await api.post(`/dispatch-tasks/${taskId}/reschedule`, {
      destination_type: data.destinationType,
      destination_id: data.destinationId,
      warehouse_appointment: data.warehouseAppointment,
      port_appointment: data.portAppointment,
      auto_resume: data.autoResume ?? true
    });
    return response.data;
  },

  /**
   * Reassign a paused task to a new driver
   * POST /api/dispatch-tasks/:id/reassign
   *
   * Design:
   * - Original task: status → 'uncompleted', route becomes origin → handover_location
   * - New task: route is handover_location → original_destination
   * - Enables accurate wage calculation for both drivers
   *
   * @param taskId - Task ID
   * @param data - Reassign data
   */
  reassignTask: async (
    taskId: number,
    data: {
      handoverLocation: string;
      handoverLocationType?: string;
      handoverLocationId?: number;
      // New destination fields (if destination is changed)
      newDestinationType?: string;
      newDestinationId?: number;
      newDestinationName?: string;
      newDestinationAddress?: string;
      reason?: string;
    }
  ): Promise<{
    originalTask: {
      id: number;
      task_id: string;
      status: string;
      uncomplete_reason: string;
      route: string;
      handover_location: string;
      reassigned_to_task_id: number;
    };
    newTask: {
      id: number;
      task_id: string;
      status: string;  // Always 'unassigned'
      driver_id: null; // Always null - driver assigned in Dispatch Console
      route: string;
      reassigned_from_task_id: number;
      // New destination info
      destination_type?: string;
      destination_id?: number;
      destination_name?: string;
      destination_address?: string;
    };
  }> => {
    const response = await api.post(`/dispatch-tasks/${taskId}/reassign`, {
      handover_location: data.handoverLocation,
      handover_location_type: data.handoverLocationType,
      handover_location_id: data.handoverLocationId,
      // Pass new destination info to backend
      new_destination_type: data.newDestinationType,
      new_destination_id: data.newDestinationId,
      new_destination_name: data.newDestinationName,
      new_destination_address: data.newDestinationAddress,
      reason: data.reason
    });
    return response.data;
  },

  /**
   * Split a paused task (DEPRECATED - use reassignTask instead)
   * POST /api/dispatch-tasks/:id/split
   *
   * @deprecated Use reassignTask instead for better route tracking and wage calculation
   * @param taskId - Task ID
   * @param data - Split data
   */
  splitTask: async (
    taskId: number,
    data: {
      newDriverId?: number;
      newTruckId?: number;
      reason?: string;
    }
  ): Promise<{ originalTask: BackendTask; newTask: BackendTask }> => {
    const response = await api.post(`/dispatch-tasks/${taskId}/split`, {
      new_driver_id: data.newDriverId,
      new_truck_id: data.newTruckId,
      reason: data.reason
    });
    return response.data;
  },

  /**
   * Reroute a task (change destination)
   * POST /api/dispatch-tasks/:id/reroute
   *
   * Changes the task destination and resets appointments.
   * Key behaviors:
   * - Syncs Order type when taskBehavior changes (liveload <-> drop)
   * - Sets pending_delivery_* fields when rerouting to yard
   *
   * @param taskId - Task ID
   * @param data - Reroute data
   */
  rerouteTask: async (
    taskId: number,
    data: {
      routeNodes?: Array<{
        type: string;
        id?: number;
        name: string;
        address?: string;
      }>;
      destinationType?: string;
      destinationId?: number;
      destinationAddress?: string;
      taskBehavior: 'drop' | 'liveload';
      reason?: string;
      // New: After yard storage, deliver to this destination
      pendingDeliveryDestinationType?: 'warehouse' | 'port';
      pendingDeliveryDestinationId?: number;
    }
  ): Promise<BackendTask & {
    route_nodes?: Record<string, unknown>[];
    subtype_changed?: boolean;
    order_type_updated?: boolean;
    pending_yard_storage?: boolean;
  }> => {
    const response = await api.post(`/dispatch-tasks/${taskId}/reroute`, {
      route_nodes: data.routeNodes,
      destination_type: data.destinationType,
      destination_id: data.destinationId,
      destination_address: data.destinationAddress,
      task_behavior: data.taskBehavior,
      reason: data.reason,
      pending_delivery_destination_type: data.pendingDeliveryDestinationType,
      pending_delivery_destination_id: data.pendingDeliveryDestinationId
    });
    return response.data;
  },

  // ==================== Rejected Handling ====================

  /**
   * Handle a rejected task (per TASK_LIFECYCLE_DESIGN.md v1.2 Section 7.3)
   * POST /api/dispatch-tasks/:id/handle-rejected
   *
   * Task must be in 'paused' status with pause_reason = 'rejected'.
   * Three possible actions:
   * - 'reschedule': Change destination and resume
   * - 'store_to_yard': Route to yard with optional pending delivery
   * - 'cancel': Cancel the task
   *
   * @param taskId - Task ID
   * @param data - Action data
   */
  handleRejected: async (
    taskId: number,
    data: {
      action: 'reschedule' | 'store_to_yard' | 'cancel';
      // For 'reschedule' action:
      destinationType?: string;
      destinationId?: number;
      warehouseAppointment?: string;
      appointmentNumber?: string;
      taskSubtype?: string;
      // For 'store_to_yard' action:
      yardId?: number;
      pendingDestinationType?: string;
      pendingDestinationId?: number;
      // For 'cancel' action:
      cancelReason?: string;
    }
  ): Promise<BackendTask> => {
    const response = await api.post(`/dispatch-tasks/${taskId}/handle-rejected`, {
      action: data.action,
      // Reschedule params
      destination_type: data.destinationType,
      destination_id: data.destinationId,
      warehouse_appointment: data.warehouseAppointment,
      appointment_number: data.appointmentNumber,
      task_subtype: data.taskSubtype,
      // Store to yard params
      yard_id: data.yardId,
      pending_destination_type: data.pendingDestinationType,
      pending_destination_id: data.pendingDestinationId,
      // Cancel params
      cancel_reason: data.cancelReason
    });
    return response.data;
  },

  // ==================== Task Pairing ====================

  /**
   * Get pairing candidates for a task
   * GET /api/dispatch-tasks/:id/pairing-candidates
   */
  getPairingCandidates: async (taskId: number): Promise<PairingCandidate[]> => {
    const response = await api.get(`/dispatch-tasks/${taskId}/pairing-candidates`);
    return response.data;
  },

  /**
   * Confirm pairing between two tasks
   * POST /api/dispatch-tasks/:id/pair
   */
  confirmPairing: async (taskId: number, pairedTaskId: number): Promise<void> => {
    await api.post(`/dispatch-tasks/${taskId}/pair`, { paired_task_id: pairedTaskId });
  },

  /**
   * Remove pairing from a task
   * DELETE /api/dispatch-tasks/:id/pair
   */
  removePairing: async (taskId: number): Promise<void> => {
    await api.delete(`/dispatch-tasks/${taskId}/pair`);
  },

  // ==================== Distance & Bobtail Operations ====================

  /**
   * Get route distance info for a task
   * GET /api/dispatch-tasks/:id/route-distance
   */
  getRouteDistance: async (taskId: number): Promise<{
    estimatedDistanceMiles: number | null;
    actualDistanceMiles: number | null;
    legs: Array<{
      nodeIndex: number;
      name: string;
      type: string;
      distanceFromPrev: number | null;
      actualDistanceFromPrev: number | null;
    }>;
  }> => {
    const response = await api.get(`/dispatch-tasks/${taskId}/route-distance`);
    return response.data;
  },

  /**
   * Get available drivers sorted by distance to task's first node
   * GET /api/dispatch-tasks/:id/driver-distances
   */
  getDriverDistances: async (taskId: number): Promise<Array<{
    driverId: number;
    driverName: string;
    estimatedMiles: number;
    currentZone: string | null;
    lastLocationUpdate: string;
  }>> => {
    const response = await api.get(`/dispatch-tasks/${taskId}/driver-distances`);
    return response.data;
  },

  /**
   * Submit actual distance from mobile app
   * POST /api/dispatch-tasks/:id/actual-distance
   */
  submitActualDistance: async (taskId: number, data: {
    legs: Array<{ nodeIndex: number; distanceMiles: number; durationMinutes?: number }>;
    totalDistanceMiles: number;
  }): Promise<{ taskId: number; actualDistanceMiles: number }> => {
    const response = await api.post(`/dispatch-tasks/${taskId}/actual-distance`, data);
    return response.data;
  },

  /**
   * Create manual payment for uncompleted task
   * POST /api/driver-payments/create-manual
   */
  createManualPayment: async (taskId: number, data: {
    amount: number;
    notes?: string;
  }): Promise<Record<string, unknown>> => {
    const response = await api.post('/driver-payments/create-manual', {
      taskId,
      amount: data.amount,
      notes: data.notes,
    });
    return response.data;
  }
};

// ==================== Status Mapping ====================
// NOTE: After migration 036, both backend and frontend use kebab-case status values
// These mapping functions are simplified but kept for backwards compatibility

/**
 * 后端状态 ↔ 前端状态。迁移到 kebab-case 之后两边已经一样，这一对函数如今
 * 只剩两件事：认历史遗留的 PascalCase，以及**在遇到不认识的值时不要说谎**。
 *
 * ⚠️ 这里曾经有一句 `|| 'released'` 兜底，代价很贵。
 *
 * 2026-09-22 线上：查验柜自动推成 `pcc-hold`，而白名单里只有 `yard-stored`、
 * 没有 `pcc-hold`。于是三个正躺在海关查验站的柜子在派单板上显示成
 * **Released** —— CBP Hold 那格是 0，Released 从 3 变成 6。调度看到的是
 * 「可以去提了」，而柜子根本不在码头。
 *
 * 症结不在「漏了一个」，在于**兜底值本身有业务含义**：把「我不认识这个状态」
 * 翻译成「这柜可以提」，等于把未知说成了最危险的那种已知。所以现在认不出来
 * 的值**原样透出**并出声 —— 界面各处取样式都是 `statusConfig[x]?.… || x`，
 * 透出的结果是一个灰色标签写着原始值，看得出「这是个我不认识的状态」，而不是
 * 一句理直气壮的错话。
 */
export const TASK_STATUSES = [
  'on-vessel', 'released', 'on-hold', 'unassigned', 'assigned',
  'picking', 'delivering', 'on-stop', 'completed', 'loading',
  'empty', 'loaded', 'returning', 'cancelled', 'paused',
  'yard-stored',   // Container stored in yard
  'pcc-hold',      // Container at the CBP exam station
  'uncompleted',   // Reassigned task - partial completion
] as const;

/** 历史遗留的 PascalCase。线上早已没有，留着只为老数据/老客户端。 */
const LEGACY_STATUS_MAP: Record<string, string> = {
  'On Vessel': 'on-vessel',
  'On Hold': 'on-hold',
  'Released': 'released',
  'Unassigned': 'unassigned',
  'Assigned': 'assigned',
  'Picking': 'picking',
  'In Progress': 'delivering',
  'Delivering': 'delivering',
  'On Stop': 'on-stop',
  'Loading': 'loading',
  'Empty': 'empty',
  'Loaded': 'loaded',
  'Returning': 'returning',
  'Completed': 'completed',
  'Cancelled': 'cancelled',
  'Paused': 'paused',
  'Pending': 'unassigned',
  'Failed': 'cancelled',
};

/** 只在开发时出声一次，不在生产控制台刷屏。 */
const warned = new Set<string>();
function warnUnknown(kind: string, value: string, where: string): void {
  const key = `${kind}:${value}`;
  if (warned.has(key)) return;
  warned.add(key);
  console.warn(
    `[taskApi] 不认识的${kind} "${value}" —— 已原样透出。` +
    `如果这是新加的值，请补进 ${where}。`
  );
}

export const mapBackendStatusToFrontend = (backendStatus: string): string => {
  if (!backendStatus) return backendStatus;
  if ((TASK_STATUSES as readonly string[]).includes(backendStatus)) return backendStatus;

  const legacy = LEGACY_STATUS_MAP[backendStatus];
  if (legacy) return legacy;

  warnUnknown('任务状态', backendStatus, 'TASK_STATUSES');
  return backendStatus;
};

/**
 * 前端状态 -> 后端状态。
 *
 * 这一侧透出比显示侧更要紧：以前不认识就发 'released' 给服务端，等于**把一个
 * 错误的状态真写进库里**。原样透出的话，后端的 CHECK 约束会当场拒绝 —— 报错
 * 总好过写错。
 */
export const mapFrontendStatusToBackend = (frontendStatus: string): string => {
  if (!frontendStatus) return frontendStatus;
  if ((TASK_STATUSES as readonly string[]).includes(frontendStatus)) return frontendStatus;

  warnUnknown('任务状态', frontendStatus, 'TASK_STATUSES');
  return frontendStatus;
};

/**
 * 后端任务类型 -> 前端任务类型映射
 * Supports both old UPPERCASE format and new kebab-case format (STATUS_DESIGN.md v3.0)
 * 6 valid task types: load-out, load-in, empty-out, empty-in, delivery, return
 */
export const TASK_TYPES = ['load-out', 'load-in', 'empty-out', 'empty-in', 'delivery', 'return'] as const;

export const mapBackendTaskTypeToFrontend = (backendType: string): string => {
  // 和状态那边同一个毛病、同一种修法：认不出来就原样透出，别猜。
  // 以前这里空值和不认识的值都会变成 'load-out'（提重柜）—— 一个有明确业务
  // 含义的值，够让人照着它去派一趟根本不存在的活。
  if (!backendType) return backendType;

  const lowered = backendType.toLowerCase();
  if ((TASK_TYPES as readonly string[]).includes(lowered)) {
    return lowered;
  }

  // Map old UPPERCASE formats to kebab-case
  const typeMap: Record<string, string> = {
    'PICK_UP': 'load-out',
    'DROP_OFF': 'load-in',
    'LOAD_IN': 'load-in',
    'LOAD_OUT': 'load-out',
    'EMPTY_IN': 'empty-in',
    'EMPTY_OUT': 'empty-out',
    'LIVE_LOAD': 'load-in',
    'RETURN': 'return',
    'DELIVERY': 'delivery'
  };
  const mapped = typeMap[backendType];
  if (mapped) return mapped;

  warnUnknown('任务类型', backendType, 'TASK_TYPES');
  return backendType;
};

/**
 * 前端任务类型 -> 后端任务类型映射
 * Backend now uses kebab-case format (STATUS_DESIGN.md v3.0)
 */
export const mapFrontendTaskTypeToBackend = (frontendType: string): string => {
  // Backend now accepts kebab-case directly
  const validTypes = ['load-out', 'load-in', 'empty-out', 'empty-in', 'delivery', 'return'];
  if (validTypes.includes(frontendType)) {
    return frontendType;
  }
  return 'load-out';
};

// ==================== Data Transformation ====================

/**
 * 将后端返回的 snake_case 字段转换为前端 camelCase
 * 用于三方角色和位置多态字段的标准化
 */
const transformBackendTask = (task: BackendTask): BackendTask & {
  // Three-party roles (camelCase)
  orderingPartyType?: 'client' | 'shipper' | 'warehouse' | 'port';
  orderingPartyId?: number;
  orderingPartyName?: string;
  billToType?: 'client' | 'shipper' | 'warehouse' | 'port';
  billToId?: number;
  billToName?: string;
  deliverToType?: 'client' | 'shipper' | 'warehouse' | 'port';
  deliverToId?: number;
  deliverToName?: string;
  // Task location (camelCase)
  originType?: 'port' | 'warehouse' | 'shipper' | 'yard';
  originId?: number;
  originName?: string;
  destinationType?: 'port' | 'warehouse' | 'shipper' | 'yard';
  destinationId?: number;
  destinationName?: string;
} => {
  return {
    ...task,
    // Three-party roles - add camelCase aliases
    orderingPartyType: task.ordering_party_type,
    orderingPartyId: task.ordering_party_id,
    orderingPartyName: task.ordering_party_name,
    billToType: task.bill_to_type,
    billToId: task.bill_to_id,
    billToName: task.bill_to_name,
    deliverToType: task.deliver_to_type,
    deliverToId: task.deliver_to_id,
    deliverToName: task.deliver_to_name,
    // Task location - add camelCase aliases
    originType: task.origin_type,
    originId: task.origin_id,
    originName: task.origin_name,
    originShortName: task.origin_short_name || '',
    originCity: task.origin_city || '',
    originAddress: task.origin_address || '',
    destinationType: task.destination_type,
    destinationId: task.destination_id,
    destinationName: task.destination_name,
    destinationShortName: task.destination_short_name || '',
    destinationCity: task.destination_city || '',
    destinationAddress: task.destination_address || '',
    // Paired task fields
    pairedTaskId: task.paired_task_id,
    pairedTaskDisplayId: task.paired_task_display_id,
    pairedTaskType: task.paired_task_type,
    pairedTaskSubtype: task.paired_task_subtype,
    pairedTaskStatus: task.paired_task_status,
    pairedContainerNumber: task.paired_container_number,
  };
};

/**
 * 批量转换任务数组
 */
export const transformBackendTasks = (tasks: BackendTask[]): ReturnType<typeof transformBackendTask>[] => {
  return tasks.map(transformBackendTask);
};

/**
 * 将前端 camelCase 字段转换为后端 snake_case
 * 用于创建/更新请求
 */
export const transformToBackendTask = (data: {
  orderingPartyType?: 'client' | 'shipper' | 'warehouse' | 'port';
  orderingPartyId?: number;
  billToType?: 'client' | 'shipper' | 'warehouse' | 'port';
  billToId?: number;
  deliverToType?: 'client' | 'shipper' | 'warehouse' | 'port';
  deliverToId?: number;
  originType?: 'port' | 'warehouse' | 'shipper' | 'yard';
  originId?: number;
  destinationType?: 'port' | 'warehouse' | 'shipper' | 'yard';
  destinationId?: number;
  [key: string]: unknown;
}): Partial<BackendTask> => {
  const result: Partial<BackendTask> & { [key: string]: unknown } = { ...data };

  // Convert camelCase to snake_case for three-party roles
  if (data.orderingPartyType !== undefined) {
    result.ordering_party_type = data.orderingPartyType;
    delete result.orderingPartyType;
  }
  if (data.orderingPartyId !== undefined) {
    result.ordering_party_id = data.orderingPartyId;
    delete result.orderingPartyId;
  }
  if (data.billToType !== undefined) {
    result.bill_to_type = data.billToType;
    delete result.billToType;
  }
  if (data.billToId !== undefined) {
    result.bill_to_id = data.billToId;
    delete result.billToId;
  }
  if (data.deliverToType !== undefined) {
    result.deliver_to_type = data.deliverToType;
    delete result.deliverToType;
  }
  if (data.deliverToId !== undefined) {
    result.deliver_to_id = data.deliverToId;
    delete result.deliverToId;
  }

  // Convert camelCase to snake_case for task location
  if (data.originType !== undefined) {
    result.origin_type = data.originType;
    delete result.originType;
  }
  if (data.originId !== undefined) {
    result.origin_id = data.originId;
    delete result.originId;
  }
  if (data.destinationType !== undefined) {
    result.destination_type = data.destinationType;
    delete result.destinationType;
  }
  if (data.destinationId !== undefined) {
    result.destination_id = data.destinationId;
    delete result.destinationId;
  }

  return result;
};

export default taskAPI;
